// Measures an element's own content width via ResizeObserver — the basis for
// the grids' progressive column hiding. We track the grid container's width
// (not the window) so column hiding follows the space actually available to the
// table: the sidebar offsets the window width, which is what let leading columns
// get squeezed off-screen. Returns 0 until first measured; callers treat 0 as
// "not measured yet → show everything" (see toResponsiveWidth).
//
// This is the CONTAINER-measured half of the responsive contract, and it is the
// preferred mechanism for anything inside the content column — see DESIGN.md
// ("Responsive"). The viewport-measured hooks in ./theme/breakpoints.ts are for
// the shell itself, which is the one thing that genuinely sizes off the viewport.
// Shared by the customer and admin apps so column shedding behaves identically
// in both.

import { useCallback, useEffect, useState } from 'react';
import type { RefCallback } from 'react';

export function useElementWidth(): [RefCallback<HTMLDivElement>, number | null] {
  // A ref *callback* (not useRef) so the observer re-attaches whenever the node
  // mounts or changes — a plain ref read inside a []-effect misses the element
  // on conditional mount paths. `width` is null until first measured, which is
  // distinct from a legitimately measured 0 (see toResponsiveWidth).
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const ref = useCallback<RefCallback<HTMLDivElement>>((el) => {
    setNode(el);
  }, []);
  useEffect(() => {
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [ref, width];
}

// Normalize a measured width for use in `w >= threshold` column-visibility
// checks: an UNMEASURED width (null) becomes +Infinity so every column shows
// until the first measurement lands (avoids a flash of the narrowest layout). A
// real measured 0 passes through unchanged.
export function toResponsiveWidth(width: number | null): number {
  return width == null ? Number.POSITIVE_INFINITY : width;
}
